import { nlt } from '../services/api';
import { NLT_ADMIN_FINAL_CONFIG } from '../constants/nltAdminFinalConfig';
import { serializeNltAdminConfigForSave } from '../constants/nltAdminAgentTools';
import {
  omitDefaultVirtualUserSystemPrompt,
  stripVirtualUserPromptFieldsFromRecord,
} from '../constants/vuserDefaults';
import type { AgentConfiguratorState } from '../types/agentConfigurator';
import type { NLTConfig } from '../types/nlt';

export interface NltAdminConfigApiPayload {
  nlt_config: Record<string, unknown>;
  nlt_config_id?: string;
}

let cachedLatestNltConfig: NLTConfig | null = null;
let latestNltConfigPromise: Promise<NLTConfig | null> | null = null;

export function invalidateLatestNltAdminConfigCache(): void {
  cachedLatestNltConfig = null;
  latestNltConfigPromise = null;
}

async function fetchLatestNltAdminConfig(): Promise<NLTConfig | null> {
  if (cachedLatestNltConfig) return cachedLatestNltConfig;
  if (!latestNltConfigPromise) {
    latestNltConfigPromise = (async () => {
      try {
        const res = await nlt.getLatestNLTConfig();
        cachedLatestNltConfig = res.success && res.data ? res.data : null;
        return cachedLatestNltConfig;
      } catch {
        return null;
      } finally {
        latestNltConfigPromise = null;
      }
    })();
  }
  return latestNltConfigPromise;
}

/** Connector config for NLT patch/chat — never inject virtual-user prompt fields. */
export function prepareConnectorConfigForNltApi(
  config: Record<string, unknown> | AgentConfiguratorState | null | undefined
): Record<string, unknown> {
  const cloned = JSON.parse(JSON.stringify(config ?? {})) as Record<string, unknown>;
  return stripVirtualUserPromptFieldsFromRecord(cloned);
}

/** Admin NLT template from Admin → NLT Configs (latest saved, else built-in default). */
export function prepareNltAdminConfigForApi(
  raw: Record<string, unknown> | AgentConfiguratorState
): Record<string, unknown> {
  const serialized = serializeNltAdminConfigForSave(raw as AgentConfiguratorState);
  return omitDefaultVirtualUserSystemPrompt(serialized);
}

/**
 * Just the id of the latest admin NLT config, for routes that resolve the config
 * themselves (`/nlt/workflow-patch`). Serializing the whole blob is both wasted work
 * and a needless failure point there — it is not part of that request body.
 */
export async function resolveNltAdminConfigId(): Promise<string | undefined> {
  try {
    return (await fetchLatestNltAdminConfig())?.id ?? undefined;
  } catch {
    return undefined;
  }
}

export async function resolveNltAdminConfigForApi(): Promise<NltAdminConfigApiPayload> {
  const latest = await fetchLatestNltAdminConfig();
  if (latest?.config && typeof latest.config === 'object') {
    return {
      nlt_config: prepareNltAdminConfigForApi(latest.config as Record<string, unknown>),
      nlt_config_id: latest.id,
    };
  }
  return {
    nlt_config: prepareNltAdminConfigForApi(NLT_ADMIN_FINAL_CONFIG),
  };
}
